/**
 * Domain → business UI profiles
 *
 * **One central definition per client** (`CLIENTS`): that object holds everything for that
 * implementation — copy under `labels`, behavior under `implementation`. Domains (Firestore
 * `run.domain` slugs) **map to a client** in `DOMAIN_TO_CLIENT`.
 *
 * **Client profiles** (`CLIENTS`): each implementation sets `supportsDecisionProfile` (business column
 * with decisions: data source, recommendation, insights, etc.) and `showBusinessDataSourcePanel`.
 * **`cp-wealth.net`** maps to the **cpWealth** client via {@link DOMAIN_TO_CLIENT}. Domains that use
 * `default` get technical-notebook-first UX (CSV upload on Technical when there is no business column).
 *
 * To add a tenant: (1) add a `CLIENTS` entry, (2) map slug(s) in `DOMAIN_TO_CLIENT`, (3) optionally
 * add `DOMAIN_SLUG_OVERRIDES` for one-off label tweaks without a new client.
 */

/** @typedef {'blockShell' | 'cpWealthRich'} BusinessLayoutVariant */

export const DEFAULT_RUN_DOMAIN = "default";

/** Firestore / API slug for CP Wealth (must match `users/{uid}.domain` for that org). */
export const CP_WEALTH_NET_DOMAIN = "cp-wealth.net";

/** Firestore / API slug for Archimedes Rx (must match `users/{uid}.domain` for that org). */
export const ARCHIMEDESRX_DOMAIN = "archimedesrx.com";

/** Alternate / legacy strings → canonical slug keys used in {@link DOMAIN_TO_CLIENT}. */
const DOMAIN_SLUG_ALIASES = /** @type {Record<string, string>} */ ({
  cpwealth: CP_WEALTH_NET_DOMAIN,
  "cp-wealth": CP_WEALTH_NET_DOMAIN,
  "cp_wealth.net": CP_WEALTH_NET_DOMAIN,
  "cpwealth.net": CP_WEALTH_NET_DOMAIN,
});

/**
 * Normalize raw `run.domain` (or hostname) for profile lookup: URLs, `www`, casing, CP Wealth aliases.
 * @param {string | null | undefined} raw
 * @returns {string}
 */
export function normalizeDomainSlugForProfile(raw) {
  let s = typeof raw === "string" ? raw.trim() : "";
  if (!s) return DEFAULT_RUN_DOMAIN;
  s = s.toLowerCase();
  try {
    if (s.includes("://") || s.startsWith("www.") || (s.includes(".") && s.includes("/"))) {
      const u = new URL(s.includes("://") ? s : `https://${s}`);
      const h = (u.hostname || "").replace(/^www\./i, "");
      if (h) s = h;
    }
  } catch {
    /* keep s as hostname-ish token */
  }
  s = s.replace(/^www\./, "");
  return DOMAIN_SLUG_ALIASES[s] ?? s;
}

function clientKeyForSlug(slug) {
  return DOMAIN_TO_CLIENT[slug] ?? "default";
}

/**
 * Resolve notebook tenant slug: **run.domain** (normalized), else Firestore **user.domain** (via
 * `user_domain` from GET /settings), else email host only when it maps to a non-default {@link CLIENTS} entry.
 *
 * @param {string | null | undefined} runDomainRaw
 * @param {string | null | undefined} userDomainRaw from API `user_domain` (Firestore `users.{uid}.domain`)
 * @param {string | null | undefined} userEmailHint e.g. MSAL `username`
 * @returns {string}
 */
export function effectiveDomainSlugForNotebook(runDomainRaw, userDomainRaw, userEmailHint) {
  const fromRun = normalizeDomainSlugForProfile(runDomainRaw);
  if (fromRun !== DEFAULT_RUN_DOMAIN) return fromRun;
  const fromUser = normalizeDomainSlugForProfile(userDomainRaw);
  if (fromUser !== DEFAULT_RUN_DOMAIN) return fromUser;
  if (typeof userEmailHint !== "string" || !userEmailHint.trim()) return DEFAULT_RUN_DOMAIN;
  const m = userEmailHint.trim().toLowerCase().match(/@([a-z0-9.-]+)$/i);
  if (!m) return DEFAULT_RUN_DOMAIN;
  const fromEmail = normalizeDomainSlugForProfile(m[1]);
  if (fromEmail === DEFAULT_RUN_DOMAIN) return DEFAULT_RUN_DOMAIN;
  if (clientKeyForSlug(fromEmail) === "default") return DEFAULT_RUN_DOMAIN;
  return fromEmail;
}

/**
 * Shared section identifiers for the business notebook column (order is the product contract).
 * Optional steps are gated in `Notebook.jsx` from the resolved profile.
 */
export const NOTEBOOK_BUSINESS_SECTION_IDS = /** @type {const} */ ([
  "dataSource",
  "recommendation",
  "insights",
  "detailedData",
]);

/**
 * @typedef {{
 *   layoutVariant: BusinessLayoutVariant;
 *   supportsDecisionProfile: boolean;
 *   showBusinessDataSourcePanel: boolean;
 *   dailyTickerPredictionsTable?: string | null;
 * }} ClientImplementation
 */

/** @type {Record<string, { implementation: ClientImplementation; labels: Record<string, unknown> }>} */
const CLIENTS = {
  default: {
    implementation: {
      layoutVariant: "blockShell",
      supportsDecisionProfile: false,
      showBusinessDataSourcePanel: false,
    },
    labels: {
      businessTabLabel: "Business decisions",
      technicalTabLabel: "Technical details",
      openTechnicalNotebookCta: "Open technical notebook",
      insightLabelLikelihood: "Likelihood",
      insightLabelKeyReasons: "Key reasons",
      insightLabelConditions: "Conditions and caveats",
      externalViews: [],
      insightSectionResult: "Results",
      insightSectionImpact: "Impact",
      insightPilotResultGroupTitle: "Pilot Result",
      insightsUnavailableMessage: "Insights are not available for this run yet.",
      recommendationHeroLabel: "Summary",
      loadingRecommendation: "Loading…",
      emptyRecommendationTitle: "No recommendation yet",
      emptyRecommendationLead: "Upload data or connect a source, or open ",
      emptyRecommendationHighlight: "Technical details",
      emptyRecommendationTrail: " to review the notebook.",
      connectDatabaseCta: "Connect database",
      sectionConfidence: "Confidence",
      sectionDrivers: "Drivers",
      sectionRisks: "Risks",
      sectionProof: "Proof",
      externalViewsSectionTitle: "Dashboards",
      summaryPlaceholder: "Summary will appear when available.",
      intakeLabelExplicitScenario: "Scenario",
      intakeDetailPrefix: "",
      sectionEntitiesTop: "Top outcomes",
      sectionAlternatives: "Other options",
      linkScrollToDetailedData: "Jump to detailed data",
      sectionPilotReference: "Reference",
      pilotReferenceSummary: "Archived context from an earlier release.",
      insightShellEmptyLine: "No data yet.",
      shellRecommendationEmptyLine: "No data yet.",
    },
  },

  cpWealth: {
    implementation: {
      layoutVariant: "cpWealthRich",
      supportsDecisionProfile: true,
      showBusinessDataSourcePanel: true,
      dailyTickerPredictionsTable: "daily_ticker_predictions",
      // Whether the Live tab carries the Stock Summary Dashboard. A config flag
      // rather than "does the session have report_html", because the grid reads
      // its rows from the dashboard-data endpoint now — keying off the report
      // would make the section vanish the moment the HTML renderer is deleted.
      stockSummaryDashboard: true,
    },
    labels: {
      businessTabLabel: "Business decisions",
      technicalTabLabel: "Technical details",
      openTechnicalNotebookCta: "Open technical notebook",
      insightLabelLikelihood: "Likelihood",
      insightLabelKeyReasons: "Key reasons",
      insightLabelConditions: "Conditions and caveats",
      externalViews: [],
      insightSectionResult: "The Result",
      insightSectionImpact: "The impact",
      insightPilotResultGroupTitle: "Pilot Result",
      insightsUnavailableMessage: "Business insights are not yet available for this run.",
      recommendationHeroLabel: "Recommendation",
      loadingRecommendation: "Loading recommendation…",
      emptyRecommendationTitle: "No business recommendation yet",
      emptyRecommendationLead: "No business recommendation available for this run yet. Use ",
      emptyRecommendationHighlight: "Data source → Upload data",
      emptyRecommendationTrail: " above to add a CSV, or connect a database below.",
      connectDatabaseCta: "Connect to database",
      sectionConfidence: "Confidence",
      sectionDrivers: "Drivers",
      sectionRisks: "Risks",
      sectionProof: "Proof",
      externalViewsSectionTitle: "Supporting dashboards",
      summaryPlaceholder: "Summary will appear here when available.",
      intakeLabelExplicitScenario: "Scenario",
      intakeDetailPrefix: "",
      sectionEntitiesTop: "Top outcomes",
      sectionAlternatives: "Other options",
      linkScrollToDetailedData: "Jump to detailed data",
      sectionPilotReference: "Pilot / reference",
      pilotReferenceSummary:
        "Earlier pilot wording and context (archive until migrated into the main decision block).",
      insightShellEmptyLine: "No data yet.",
      shellRecommendationEmptyLine: "No data yet.",
    },
  },

  archimedesrx: {
    implementation: {
      layoutVariant: "cpWealthRich",
      supportsDecisionProfile: true,
      showBusinessDataSourcePanel: true,
    },
    labels: {
      businessTabLabel: "Business decisions",
      technicalTabLabel: "Technical details",
      openTechnicalNotebookCta: "Open technical notebook",
      insightLabelLikelihood: "Likelihood",
      insightLabelKeyReasons: "Key reasons",
      insightLabelConditions: "Conditions and caveats",
      externalViews: [],
      insightSectionResult: "Results",
      insightSectionImpact: "Impact",
      insightPilotResultGroupTitle: "Pilot Result",
      insightsUnavailableMessage: "Business insights are not yet available for this run.",
      recommendationHeroLabel: "Recommendation",
      loadingRecommendation: "Loading recommendation…",
      emptyRecommendationTitle: "No business recommendation yet",
      emptyRecommendationLead: "No business recommendation available for this run yet. Use ",
      emptyRecommendationHighlight: "Data source → Upload data",
      emptyRecommendationTrail: " above to add a CSV, or connect a database below.",
      connectDatabaseCta: "Connect to database",
      sectionConfidence: "Confidence",
      sectionDrivers: "Drivers",
      sectionRisks: "Risks",
      sectionProof: "Proof",
      externalViewsSectionTitle: "Supporting dashboards",
      summaryPlaceholder: "Summary will appear here when available.",
      intakeLabelExplicitScenario: "Scenario",
      intakeDetailPrefix: "",
      sectionEntitiesTop: "Top outcomes",
      sectionAlternatives: "Other options",
      linkScrollToDetailedData: "Jump to detailed data",
      sectionPilotReference: "Reference",
      pilotReferenceSummary: "Archived context from an earlier release.",
      insightShellEmptyLine: "No data yet.",
      shellRecommendationEmptyLine: "No data yet.",
    },
  },
};

/**
 * Map API `run.domain` slug → client key in {@link CLIENTS}. Unlisted slugs use `default`.
 * Add multiple keys here only when {@link normalizeDomainSlugForProfile} should not collapse them.
 */
const DOMAIN_TO_CLIENT = /** @type {Record<string, keyof typeof CLIENTS>} */ ({
  [DEFAULT_RUN_DOMAIN]: "default",
  [CP_WEALTH_NET_DOMAIN]: "cpWealth",
  [ARCHIMEDESRX_DOMAIN]: "archimedesrx",
});

/**
 * Per-slug tweaks without defining a full client (labels and/or implementation patches).
 * @type {Record<string, { labels?: Record<string, unknown>; implementation?: Partial<ClientImplementation> }>}
 */
const DOMAIN_SLUG_OVERRIDES = {
  // acme: { labels: { recommendationHeroLabel: "Next best action" } },
};

/** @typedef {ReturnType<typeof getDomainProfile>} DomainProfile */

/**
 * @param {string | null | undefined} domain
 * @returns {DomainProfile}
 */
export function getDomainProfile(domain) {
  const slug = normalizeDomainSlugForProfile(domain);

  const clientKey = DOMAIN_TO_CLIENT[slug] ?? "default";
  const base = CLIENTS[clientKey];
  const extra = DOMAIN_SLUG_OVERRIDES[slug];

  const labels = { ...base.labels, ...(extra?.labels && typeof extra.labels === "object" ? extra.labels : {}) };
  const implementation = {
    ...base.implementation,
    ...(extra?.implementation && typeof extra.implementation === "object" ? extra.implementation : {}),
  };

  return /** @type {DomainProfile} */ ({
    id: slug,
    ...labels,
    ...implementation,
  });
}
